import React, { useState } from 'react';
import siteContent from '../../content/site/content.json';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle, Loader2, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import PainPointSurvey from '../forms/PainPointSurvey';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'resident', label: 'Resident' },
  { value: 'housing_provider', label: 'Housing provider' },
  { value: 'resident_association', label: 'Resident group' },
  { value: 'advice_organisation', label: 'Advice / community organisation' },
  { value: 'partner', label: 'Partner' },
];

const LaunchUpdatesSection = () => {
  const d = siteContent.launchUpdates || {};
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [interest, setInterest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showSurvey, setShowSurvey] = useState(false);

  // Deep link: /#survey opens the collapsed survey and scrolls to it
  React.useEffect(() => {
    if (window.location.hash === '#survey') {
      setShowSurvey(true);
      setTimeout(() => {
        document.getElementById('survey')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/waiting-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '',
          email,
          user_type: role,
          desired_features: interest ? `Area of interest: ${interest}` : '',
          subscribe_blog: true,
          interest_type: 'launch_updates',
          source_page: (typeof window !== 'undefined' ? window.location.pathname : '/'),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to submit');
      }
      setIsSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="updates" className="py-16 lg:py-20 bg-[#FFF8F5] scroll-mt-20" data-testid="launch-updates-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-5">
            <Mail className="w-4 h-4 text-[#B5541B]" />
            <span className="text-sm font-semibold text-[#B5541B]">Launch updates</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] leading-tight mb-3">{d.heading}</h2>
          <p className="text-base text-[#3B3535]/70 max-w-xl mx-auto leading-relaxed">{d.description}</p>
        </div>

        {isSuccess ? (
          <div data-testid="updates-success" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <p className="text-[#1F1E1E] font-semibold">{d.successMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 space-y-4" data-testid="updates-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="updates-email" className="text-[#3B3535] font-medium">Email <span className="text-[#B5541B]">*</span></Label>
                <Input
                  id="updates-email"
                  data-testid="updates-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3B3535] font-medium">I am a... <span className="text-[#B5541B]">*</span></Label>
                <Select required value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="updates-role-select" className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="updates-interest" className="text-[#3B3535] font-medium">
                What are you most interested in? <span className="text-[#3B3535]/50">(optional)</span>
              </Label>
              <Input
                id="updates-interest"
                data-testid="updates-interest-input"
                type="text"
                value={interest}
                onChange={(e) => setInterest(e.target.value)}
                placeholder="e.g. repairs tracking, resident rights, future pilots"
                className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
              />
            </div>
            {error && (
              <div data-testid="updates-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" role="alert">{error}</div>
            )}
            <Button
              type="submit"
              data-testid="updates-submit-btn"
              disabled={isSubmitting || !email || !role}
              className="w-full bg-[#1F1E1E] hover:bg-black text-white font-semibold py-5 rounded-full shadow-md transition-all duration-300 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Joining...
                </>
              ) : (
                'Get launch updates'
              )}
            </Button>
          </form>
        )}

        {/* Optional research survey (kept separate from pilot application & updates) */}
        <div id="survey" className="mt-10 scroll-mt-24">
          <div className="text-center">
            <p className="text-sm text-[#3B3535]/70 mb-3">{d.surveyPrompt}</p>
            <button
              type="button"
              data-testid="survey-toggle-btn"
              onClick={() => setShowSurvey((s) => !s)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors"
            >
              {d.surveyToggleLabel}
              {showSurvey ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          {showSurvey && (
            <div className="mt-6" data-testid="survey-container">
              <PainPointSurvey />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LaunchUpdatesSection;
